/**
 * Image processing utilities for chat attachments
 */

export interface ProcessedImage {
  dataUrl: string;
  mimeType: string;
  sizeBytes: number;
  width: number;
  height: number;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Process an image file and return metadata
 */
export const processImage = async (file: File): Promise<ProcessedImage> => {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Validate size (5MB)
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('Image must be less than 5MB');
  }

  // Read as data URL
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Get image dimensions
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataUrl;
  });

  return {
    dataUrl,
    mimeType: file.type,
    sizeBytes: file.size,
    width: img.width,
    height: img.height,
  };
};

/**
 * Compress an image if it's too large
 */
export const compressImage = async (dataUrl: string, maxWidth = 1024): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Create canvas for compression
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions (resize if needed, otherwise keep original)
      let targetWidth = img.width;
      let targetHeight = img.height;

      if (img.width > maxWidth || img.height > maxWidth) {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        targetWidth = Math.floor(img.width * ratio);
        targetHeight = Math.floor(img.height * ratio);
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Draw and compress - always convert to JPEG to reduce size
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert to JPEG with 85% quality (preserves visual details for vision models)
      // Backend will further optimize based on detail level (high=500KB, low=150KB)
      const compressed = canvas.toDataURL('image/jpeg', 0.85);
      resolve(compressed);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
};

/**
 * Convert a Blob to base64 data URL
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Validate image file format
 */
export const isValidImageFormat = (mimeType: string): boolean => {
  const validFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
  return validFormats.includes(mimeType);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
